/* @policy: WEBSITE_CMS — production city-data adapter (Seoul weather and sunrise/sunset) for the shell's window.havehadCityData contract.
 *
 * Seoul MET Norway connection. Free commercial use with attribution; no key.
 * An explicit havehadCityDataConfig with HTTPS weatherUrl/airUrl overrides the defaults;
 * {enabled:false} disables requests. API keys must never be put in browser URLs.
 *
 * Exposes (same contract as the preview adapter):
 *   window.havehadCityData.latest   -> frozen snapshot
 *   window.havehadCityData.refresh()-> Promise<snapshot>, never rejects
 *   CustomEvent 'havehad-city-data' on window, detail = snapshot, fired on change
 * Snapshot: { weather:{state,temperature,weatherCode,isDay,rain1h,sunrise,sunset,sourceTime},
 *             air:{state,pm25,pm10,sourceTime}, attribution, fetchedAt }
 * state = 'loading' | 'ready' | 'error'. Failed, stale or invalid fields are null, never 0.
 */
(function () {
  'use strict';

  if (window.havehadCityData) return;

  function httpsUrl(v) {
    if (typeof v !== 'string') return null;
    try { var u = new URL(v); return u.protocol === 'https:' && !u.username && !u.password && !u.searchParams.has('apikey') && !u.searchParams.has('serviceKey') ? u.href : null; } catch (e) { return null; }
  }

  var cfg = window.havehadCityDataConfig || {
    provider: 'met-norway',
    weatherUrl: 'https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=37.5665&lon=126.9780'
  };
  if (cfg.enabled === false) return;
  var MET = cfg.provider === 'met-norway';
  var URLS = { weather: httpsUrl(cfg.weatherUrl), air: httpsUrl(cfg.airUrl) };
  if (!URLS.weather && !URLS.air) return;
  var MODELED = cfg.provider === 'open-meteo' || [URLS.weather, URLS.air].some(function (url) { return url && /^(api|air-quality-api)\.open-meteo\.com$/.test(new URL(url).hostname); });
  var ATTRIBUTION = typeof cfg.attribution === 'string' && cfg.attribution ? cfg.attribution : (MET ? 'MET Norway · CC BY 4.0' : MODELED ? 'Weather: Open-Meteo · Air quality: CAMS via Open-Meteo' : null);

  var TIMEOUT_MS = 10000;
  var REFRESH_MS = 15 * 60 * 1000;
  var HOUR_MS = 60 * 60 * 1000;
  var MAX_AGE_MS = { weather: 2 * HOUR_MS, air: 4 * HOUR_MS };
  var MAX_FUTURE_MS = 30 * 60 * 1000;
  var SEOUL_OFFSET_S = 9 * 3600;
  var WMO_CODES = [0, 1, 2, 3, 45, 48, 51, 53, 55, 56, 57, 61, 63, 65, 66, 67,
    71, 73, 75, 77, 80, 81, 82, 85, 86, 95, 96, 99];

  var weather = emptyWeather('loading');
  var air = emptyAir(MET ? 'disabled' : 'loading');
  var solarData = emptySolar('loading');
  var successAt = { weather: null, air: null, solar: null };
  var inflight = { weather: null, air: null, solar: null };
  var nextAllowedAt = { weather: 0, solar: 0 };
  var requestedSolarDate = null;
  var cycle = null;
  var timer = null;
  var expiryTimer = null;
  var lastCycleStart = 0;
  var latest = build();
  var lastKey = JSON.stringify(latest);

  function emptyWeather(state) {
    return { state: state, temperature: null, weatherCode: null, isDay: null,
      rain1h: null, sunrise: null, sunset: null, sourceTime: null };
  }
  function emptyAir(state) {
    return { state: state, pm25: null, pm10: null, sourceTime: null };
  }
  function emptySolar(state) { return { state: state, date: null, sunrise: null, sunset: null }; }
  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function inList(list, v) { return list.indexOf(v) !== -1; }

  // 'YYYY-MM-DDTHH:mm' (Seoul local) -> epoch ms, parsed with explicit +09:00.
  function parseSeoul(s) {
    if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return null;
    var ms = Date.parse(s + ':00+09:00');
    return isFinite(ms) && new Date(ms + SEOUL_OFFSET_S * 1000).toISOString().slice(0, 16) === s ? ms : null;
  }
  function seoulDate(ms) { return new Date(ms + SEOUL_OFFSET_S * 1000).toISOString().slice(0, 10); }
  function fresh(ms, now, maxAge) { var age = now - ms; return age <= maxAge && age >= -MAX_FUTURE_MS; }
  function hhmm(s, date) {
    if (typeof s !== 'string') return null;
    var m = /^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
    return m && m[1] === date ? m[2] + ':' + m[3] : null;
  }

  // Hourly precipitation = sum of the preceding hour at that stamp.
  function rainLastHour(j, curTime) {
    var h = j.hourly, hu = j.hourly_units;
    if (!h || !Array.isArray(h.time) || !Array.isArray(h.precipitation) || h.time.length !== h.precipitation.length) return null;
    if (!hu || hu.precipitation !== 'mm') return null;
    var hourKey = curTime.slice(0, 13) + ':00', idx = -1;
    for (var i = 0; i < h.time.length; i++) {
      var t = h.time[i];
      if (typeof t === 'string' && t <= curTime && (idx < 0 || t > h.time[idx])) idx = i;
    }
    if (idx < 0 || h.time[idx] !== hourKey) return null;
    var v = h.precipitation[idx];
    return isNum(v) && v >= 0 ? v : null;
  }

  // Only the entry for the real current Seoul date; never a neighbouring day.
  function solar(j, now) {
    var d = j && j.daily, none = emptySolar('error');
    if (!j || j.utc_offset_seconds !== SEOUL_OFFSET_S || !d || !Array.isArray(d.time) || !Array.isArray(d.sunrise) || !Array.isArray(d.sunset)) return none;
    if (d.time.length !== d.sunrise.length || d.time.length !== d.sunset.length) return none;
    var today = seoulDate(now), idx = d.time.indexOf(today);
    if (d.time.lastIndexOf(today) !== idx) return none;
    if (idx < 0) return none;
    var sr = hhmm(d.sunrise[idx], today), ss = hhmm(d.sunset[idx], today);
    return sr && ss && sr < ss ? { state: 'ready', date: today, sunrise: sr, sunset: ss } : none;
  }

  function parseWeather(j, now) {
    var c = j && j.current, u = j && j.current_units;
    if (!c || !u || typeof c !== 'object') throw new Error('schema');
    if (j.utc_offset_seconds !== SEOUL_OFFSET_S) throw new Error('timezone');
    var ms = parseSeoul(c.time);
    if (ms === null || !fresh(ms, now, MAX_AGE_MS.weather)) throw new Error('stale');
    if (u.temperature_2m !== '°C') throw new Error('unit');
    var temp = c.temperature_2m;
    if (!isNum(temp) || temp < -80 || temp > 60) throw new Error('temperature');
    var code = c.weather_code;
    if (!isNum(code) || !inList(WMO_CODES, code)) throw new Error('code');
    if (c.is_day !== 0 && c.is_day !== 1) throw new Error('is_day');
    var s = solar(j, now);
    return { state: 'ready', temperature: temp, weatherCode: code, isDay: c.is_day === 1,
      rain1h: rainLastHour(j, c.time), sunrise: s.sunrise, sunset: s.sunset, sourceTime: c.time };
  }

  function parseAir(j, now) {
    var c = j && j.current, u = j && j.current_units;
    if (!c || !u || typeof c !== 'object') throw new Error('schema');
    if (j.utc_offset_seconds !== SEOUL_OFFSET_S) throw new Error('timezone');
    var ms = parseSeoul(c.time);
    if (ms === null || !fresh(ms, now, MAX_AGE_MS.air)) throw new Error('stale');
    var mu = ['μg/m³', 'µg/m³']; // Greek mu / micro sign
    if (!inList(mu, u.pm2_5) || !inList(mu, u.pm10)) throw new Error('unit');
    if (!isNum(c.pm2_5) || c.pm2_5 < 0 || !isNum(c.pm10) || c.pm10 < 0) throw new Error('pm');
    return { state: 'ready', pm25: c.pm2_5, pm10: c.pm10, sourceTime: c.time };
  }

  function metWeather(j, now) {
    var p = j && j.properties, units = p && p.meta && p.meta.units;
    if (!units || units.air_temperature !== 'celsius' || !Array.isArray(p.timeseries)) throw new Error('schema');
    var selected = null, selectedMs = -Infinity;
    p.timeseries.forEach(function (row) {
      var ms = typeof row.time === 'string' ? Date.parse(row.time) : NaN;
      if (isFinite(ms) && ms <= now && ms > selectedMs) { selected = row; selectedMs = ms; }
    });
    if (!selected || !fresh(selectedMs, now, MAX_AGE_MS.weather)) throw new Error('stale');
    var d = selected.data, instant = d && d.instant && d.instant.details;
    var temp = instant && instant.air_temperature;
    if (!isNum(temp) || temp < -80 || temp > 60) throw new Error('temperature');
    var next = d.next_1_hours, symbol = next && next.summary && next.summary.symbol_code;
    var base = typeof symbol === 'string' ? symbol.replace(/_(day|night|polartwilight)$/, '') : '';
    var codes = { clearsky:0, fair:1, partlycloudy:2, cloudy:3, fog:45,
      lightrain:61, rain:63, heavyrain:65, lightrainshowers:80, rainshowers:81, heavyrainshowers:82,
      lightsnow:71, snow:73, heavysnow:75, lightsnowshowers:85, snowshowers:86, heavysnowshowers:86,
      lightsleet:68, sleet:68, heavysleet:69, lightsleetshowers:68, sleetshowers:68, heavysleetshowers:69 };
    var code = /thunder/.test(base) ? 95 : Object.prototype.hasOwnProperty.call(codes, base) ? codes[base] : null;
    var rain = next && next.details && next.details.precipitation_amount;
    return { state:'ready', temperature:temp, weatherCode:code, isDay:typeof symbol === 'string' ? !/_night$/.test(symbol) : null,
      rain1h:units.precipitation_amount === 'mm' && isNum(rain) && rain >= 0 ? rain : null,
      rainPeriod:'next-1h', sunrise:null, sunset:null,
      sourceTime:new Date(selectedMs + SEOUL_OFFSET_S * 1000).toISOString().slice(0,16) };
  }

  function metSolar(j, now) {
    var p = j && j.properties, sr = p && p.sunrise && p.sunrise.time, ss = p && p.sunset && p.sunset.time;
    var rise = typeof sr === 'string' ? Date.parse(sr) : NaN, set = typeof ss === 'string' ? Date.parse(ss) : NaN;
    if (!isFinite(rise) || !isFinite(set) || rise >= set || seoulDate(rise) !== seoulDate(now) || seoulDate(set) !== seoulDate(now)) throw new Error('solar');
    return { state:'ready', date:seoulDate(now), sunrise:new Date(rise + SEOUL_OFFSET_S * 1000).toISOString().slice(11,16), sunset:new Date(set + SEOUL_OFFSET_S * 1000).toISOString().slice(11,16) };
  }

  var PARSERS = { weather: parseWeather, air: parseAir };

  function fetchJson(url) {
    return new Promise(function (resolve, reject) {
      if (typeof fetch !== 'function' || typeof AbortController !== 'function') { reject(new Error('unsupported')); return; }
      var ctl = new AbortController();
      var t = setTimeout(function () { ctl.abort(); reject(new Error('timeout')); }, TIMEOUT_MS);
      fetch(url, { signal: ctl.signal, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer', redirect: 'error' })
        .then(function (res) { if (!res.ok) throw new Error('http ' + res.status); return res.json().then(function (body) { return { body:body, expires:Date.parse(res.headers.get('Expires')) }; }); })
        .then(function (json) { clearTimeout(t); resolve(json); }, function (err) { clearTimeout(t); reject(err); });
    });
  }

  function build() {
    var times = [successAt.weather, successAt.air, successAt.solar].filter(Boolean).sort();
    return Object.freeze({
      provider: MET ? 'met-norway' : MODELED ? 'open-meteo' : 'configured',
      airEnabled: !MET,
      weather: Object.freeze(Object.assign({}, weather, { conditionKind: MET ? 'forecast' : MODELED ? 'model' : null })),
      air: Object.freeze(Object.assign({}, air)),
      solar: Object.freeze(Object.assign({}, solarData)),
      attribution: ATTRIBUTION,
      fetchedAt: times.length ? times[times.length - 1] : null
    });
  }

  function publish() {
    var snap = build(), key = JSON.stringify(snap);
    if (key === lastKey) return;
    lastKey = key;
    latest = snap;
    try { window.dispatchEvent(new CustomEvent('havehad-city-data', { detail: snap })); } catch (e) { /* listener errors must not break the adapter */ }
  }

  function apply(kind, result) {
    var ok = result !== null;
    if (kind === 'solar') solarData = ok ? result : emptySolar('error');
    else if (kind === 'weather') weather = ok ? result : emptyWeather('error');
    else air = ok ? result : emptyAir('error');
    successAt[kind] = ok ? new Date().toISOString() : null;
    publish();
  }

  function load(kind) {
    if (MET) {
      var today = seoulDate(Date.now());
      if (kind === 'solar' && requestedSolarDate !== today) nextAllowedAt.solar = 0;
      if (Date.now() < nextAllowedAt[kind]) return Promise.resolve();
    }
    var url = MET && kind === 'solar' ? 'https://api.met.no/weatherapi/sunrise/3.0/sun?lat=37.5665&lon=126.9780&date=' + seoulDate(Date.now()) + '&offset=%2B09%3A00' : URLS[kind];
    if (!url) { if (kind === 'weather') solarData = emptySolar('error'); apply(kind, null); return Promise.resolve(); }
    if (inflight[kind]) return inflight[kind];
    var received = false;
    var p = fetchJson(url)
      .then(function (packet) {
        received = true;
        if (MET) {
          nextAllowedAt[kind] = isFinite(packet.expires) ? packet.expires : Date.now() + REFRESH_MS;
          if (kind === 'solar') requestedSolarDate = seoulDate(Date.now());
          return kind === 'weather' ? metWeather(packet.body, Date.now()) : metSolar(packet.body, Date.now());
        }
        if (kind === 'weather') solarData = solar(packet.body, Date.now());
        return PARSERS[kind](packet.body, Date.now());
      })
      .then(function (result) { apply(kind, result); }, function () { if (!MET && kind === 'weather' && !received) solarData = emptySolar('error'); apply(kind, null); })
      .catch(function () { /* no unhandled rejections */ })
      .then(function () { inflight[kind] = null; });
    inflight[kind] = p;
    return p;
  }

  function expire() {
    var now = Date.now();
    if (weather.state === 'ready' && !fresh(parseSeoul(weather.sourceTime), now, MAX_AGE_MS.weather)) weather = emptyWeather('error');
    if (air.state === 'ready' && !fresh(parseSeoul(air.sourceTime), now, MAX_AGE_MS.air)) air = emptyAir('error');
    if (solarData.state === 'ready' && solarData.date !== seoulDate(now)) {
      solarData = emptySolar('error'); weather.sunrise = null; weather.sunset = null;
    }
    publish();
  }
  function scheduleExpiry() {
    clearTimeout(expiryTimer);
    var now = Date.now(), deadlines = [Date.parse(seoulDate(now) + 'T00:00:00+09:00') + 24 * HOUR_MS + 1];
    if (weather.state === 'ready') deadlines.push(parseSeoul(weather.sourceTime) + MAX_AGE_MS.weather + 1);
    if (air.state === 'ready') deadlines.push(parseSeoul(air.sourceTime) + MAX_AGE_MS.air + 1);
    expiryTimer = setTimeout(function () { expire(); scheduleExpiry(); }, Math.max(1, Math.min.apply(Math, deadlines) - now));
  }
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      if (!document.hidden) refresh(); // hidden tab: visibilitychange refreshes once visible and stale
    }, REFRESH_MS);
  }

  function refresh() {
    if (cycle) return cycle;
    lastCycleStart = Date.now();
    clearTimeout(timer);
    cycle = Promise.all(MET ? [load('weather'), load('solar')] : [load('weather'), load('air')])
      .catch(function () { /* load() already swallows */ })
      .then(function () { cycle = null; expire(); schedule(); scheduleExpiry(); return latest; });
    return cycle;
  }

  document.addEventListener('visibilitychange', function () {
    expire();
    if (!document.hidden && !cycle && Date.now() - lastCycleStart > REFRESH_MS) refresh();
  });

  var api = { refresh: refresh };
  Object.defineProperty(api, 'latest', { enumerable: true, get: function () { expire(); return latest; } });
  window.havehadCityData = api;

  refresh();
})();
